/**
 * Lazy loader for pdfmake.
 *
 * pdfmake bundled with its Roboto fonts is ~3.2 MB and is only needed when a PDF
 * is actually generated. Importing it dynamically keeps it out of the main app
 * bundle — it is fetched on demand the first time a PDF is built, and the
 * instance (with its base font VFS already registered) is cached for the session
 * so every caller shares the same pdfmake singleton.
 */

// @types/pdfmake declares these modules with `export =`, so the module *type* is
// the value itself, while at runtime the CJS->ESM interop exposes that value
// under `.default`. We read `.default` defensively (some bundlers hand back the
// value directly) — the same pattern used for the dynamic standard-font imports.
type PdfMakeStatic = typeof import('pdfmake/build/pdfmake');
type PdfFontsVfs = typeof import('pdfmake/build/vfs_fonts');

function interopDefault<T>(mod: unknown): T {
    const m = mod as { default?: T };
    return (m.default ?? (mod as T)) as T;
}

let loadPromise: Promise<{ pdfMake: PdfMakeStatic; pdfFonts: PdfFontsVfs }> | null = null;

/**
 * Load (once) and return the shared pdfmake instance plus its bundled font VFS.
 * The base Roboto VFS is registered on first load. Subsequent calls reuse the
 * same cached instance.
 */
export function loadPdfMake(): Promise<{ pdfMake: PdfMakeStatic; pdfFonts: PdfFontsVfs }> {
    if (!loadPromise) {
        loadPromise = (async () => {
            const [pdfMakeMod, pdfFontsMod] = await Promise.all([
                import('pdfmake/build/pdfmake'),
                import('pdfmake/build/vfs_fonts'),
            ]);
            const pdfMake = interopDefault<PdfMakeStatic>(pdfMakeMod);
            const pdfFonts = interopDefault<PdfFontsVfs>(pdfFontsMod);
            // Register bundled Roboto fonts via pdfmake's VFS. This avoids loading
            // fonts from a CDN, which would be blocked by CSP. pdfmake's default
            // font config references Roboto-Regular.ttf etc. from the VFS.
            pdfMake.addVirtualFileSystem(pdfFonts);
            return { pdfMake, pdfFonts };
        })();
    }
    return loadPromise;
}

/** The shared pdfmake instance, with its base font VFS already registered. */
export async function getPdfMake(): Promise<PdfMakeStatic> {
    return (await loadPdfMake()).pdfMake;
}
